import React, { useState, useEffect } from 'react';

/**
 * PProAuthWall
 *
 * Intercepta el acceso en el entorno de Pre-producción (PPRO) si el usuario
 * aún no ha introducido las credenciales de acceso restringido.
 * Detecta si el hostname contiene 'ppro', la ruta comienza por '/ppro',
 * o si está activada la bandera de entorno VITE_PPRO_AUTH.
 */
export const PProAuthWall: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(true);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isPproEnv, setIsPproEnv] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    const isPpro =
      hostname.includes('ppro') ||
      pathname.startsWith('/ppro') ||
      pathname.includes('/ppro') ||
      import.meta.env.VITE_PPRO_AUTH === 'true';

    setIsPproEnv(isPpro);

    if (isPpro) {
      const storedAuth = localStorage.getItem('ppro_auth');
      if (!storedAuth) {
        setIsAuthenticated(false);
      }
    }

    const handleAuthRequired = () => {
      setIsPproEnv(true);
      setIsAuthenticated(false);
      setError('Credenciales de Pre-producción requeridas. Por favor introduce usuario y contraseña.');
    };

    window.addEventListener('ppro-auth-required', handleAuthRequired);
    return () => {
      window.removeEventListener('ppro-auth-required', handleAuthRequired);
    };
  }, []);

  if (!isPproEnv || isAuthenticated) {
    return null;
  }

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!username.trim() || !password.trim()) {
      setError('Por favor introduce usuario y contraseña.');
      return;
    }

    const token = btoa(`${username.trim()}:${password.trim()}`);

    // Guardar en localStorage y en Cookie
    try {
      localStorage.setItem('ppro_auth', token);
      document.cookie = `ppro_auth=${token}; path=/; max-age=86400; SameSite=Lax`;
      setIsAuthenticated(true);
      // Recargar para sincronizar cabeceras e instancias
      window.location.reload();
    } catch {
      setError('Error al guardar credenciales locales.');
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4 font-sans">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-100 p-8 text-center animate-in fade-in zoom-in-95 duration-200">
        <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-inner">
          <svg
            className="w-7 h-7"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <h2 className="text-2xl font-bold text-slate-800 mb-1">
          Acceso a Pre-producción
        </h2>
        <p className="text-sm text-slate-500 mb-6">
          Este entorno está restringido a usuarios autorizados. Por favor ingresa tus credenciales.
        </p>

        {error && (
          <div className="mb-5 p-3.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl text-left flex items-start gap-2">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          <div>
            <label htmlFor="ppro-username" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Usuario
            </label>
            <input
              id="ppro-username"
              type="text"
              required
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
              placeholder="Introduce tu usuario"
            />
          </div>

          <div>
            <label htmlFor="ppro-password" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Contraseña
            </label>
            <input
              id="ppro-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            className="w-full mt-2 py-3 px-4 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] text-white font-medium text-sm rounded-xl shadow-lg shadow-indigo-200 transition-all cursor-pointer"
          >
            Entrar a Pre-producción
          </button>
        </form>
      </div>
    </div>
  );
};
